import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parametersAPI } from '../services/api';
import { getLocalized } from '../i18n/getLocalized';

/**
 * Currency display names, resolved to the active UI language.
 *
 * A contract stores only the ISO code ("TRY") — that is the language-independent
 * identity. The human-readable name lives on the Currency definition and is
 * multilang, so any screen that wants to *label* a currency (rather than just
 * tag an amount with its code) has to look it up and resolve it per language.
 *
 * The definitions are tenant-wide and rarely change, so the fetched list is
 * shared process-wide: the first hook instance loads it, later ones reuse it
 * instead of issuing the same request per screen. The resolver is re-created
 * whenever the language changes so consumers re-render into the new language.
 *
 * Returns:
 *   currencyName(code)  — "Türk Lirası" (falls back to the code when unknown)
 *   currencyLabel(code) — "TRY – Türk Lirası" (just "TRY" when unknown)
 */

// Shared across hook instances: the resolved list, plus the in-flight request
// so concurrent mounts don't each fire one.
let cachedCurrencies = null;
let inFlight = null;

function loadCurrencies() {
  if (cachedCurrencies) return Promise.resolve(cachedCurrencies);
  if (!inFlight) {
    inFlight = parametersAPI.currencies.list()
      .then((r) => {
        cachedCurrencies = r.data.items || [];
        return cachedCurrencies;
      })
      .catch(() => [])
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Drop the shared list — call after a currency definition is created/edited. */
export function invalidateCurrencyNames() {
  cachedCurrencies = null;
}

export default function useCurrencyNames() {
  const { i18n } = useTranslation();
  const [currencies, setCurrencies] = useState(cachedCurrencies || []);

  useEffect(() => {
    let alive = true;
    loadCurrencies().then((list) => { if (alive) setCurrencies(list); });
    return () => { alive = false; };
  }, []);

  const currencyName = useCallback((code) => {
    const key = String(code || '').trim().toUpperCase();
    if (!key) return '';
    const match = currencies.find((c) => String(c.code || '').toUpperCase() === key);
    return getLocalized(match?.name, i18n.language) || key;
  }, [currencies, i18n.language]);

  const currencyLabel = useCallback((code) => {
    const key = String(code || '').trim().toUpperCase();
    if (!key) return '';
    const name = currencyName(key);
    return name && name !== key ? `${key} – ${name}` : key;
  }, [currencyName]);

  return { currencies, currencyName, currencyLabel };
}
